import { useState, useEffect, useMemo, useCallback } from "react";
import type { ColorOption, SelectDigitOption } from "./SelectColorInput.types";
import {
  extractRalCode,
  buildGroupedOptions,
  sortedKeys,
} from "./ralColorPickerUtils";

export interface UseRalColorPickerReturn {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  firstTwo: string | null;
  thirdDigit: string | null;
  fourthDigit: string | null;
  firstTwoDigitOptions: SelectDigitOption[];
  thirdDigitOptions: SelectDigitOption[];
  fourthDigitOptions: SelectDigitOption[];
  onFirstTwoChange: (opt: SelectDigitOption | null) => void;
  onThirdDigitChange: (opt: SelectDigitOption | null) => void;
  onFourthDigitChange: (opt: SelectDigitOption | null) => void;
}

export function useRalColorPicker(
  options: ColorOption[],
  value: ColorOption | null | undefined,
  handleChange: (option: ColorOption) => void,
): UseRalColorPickerReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [firstTwo, setFirstTwo] = useState<string | null>(null);
  const [thirdDigit, setThirdDigit] = useState<string | null>(null);
  const [fourthDigit, setFourthDigit] = useState<string | null>(null);

  const groupedOptions = useMemo(() => buildGroupedOptions(options), [options]);

  // Sync internal digit selections when the external value changes
  useEffect(() => {
    const code = extractRalCode(value?.value);
    if (!code || code.length !== 4) {
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFirstTwo(code.slice(0, 2));
    setThirdDigit(code[2]);
    setFourthDigit(code[3]);
  }, [value?.value]);

  const firstTwoDigitOptions = useMemo<SelectDigitOption[]>(
    () => sortedKeys(groupedOptions).map((f2) => ({ label: f2, value: f2 })),
    [groupedOptions],
  );

  const thirdDigitOptions = useMemo<SelectDigitOption[]>(() => {
    if (!firstTwo || !groupedOptions[firstTwo]) {
      return [];
    }
    return sortedKeys(groupedOptions[firstTwo]).map((d3) => ({
      label: d3,
      value: d3,
    }));
  }, [firstTwo, groupedOptions]);

  const fourthDigitOptions = useMemo<SelectDigitOption[]>(() => {
    if (!firstTwo || !thirdDigit || !groupedOptions[firstTwo]?.[thirdDigit]) {
      return [];
    }
    return sortedKeys(groupedOptions[firstTwo][thirdDigit]).map((d4) => ({
      label: d4,
      value: d4,
    }));
  }, [firstTwo, thirdDigit, groupedOptions]);

  const commitSelection = useCallback(
    (f2: string, d3: string, d4: string) => {
      const selected = groupedOptions[f2]?.[d3]?.[d4];
      if (selected) {
        handleChange(selected);
      }
    },
    [groupedOptions, handleChange],
  );

  const onFirstTwoChange = useCallback(
    (opt: SelectDigitOption | null) => {
      const newF2 = opt?.value;
      if (!newF2 || !groupedOptions[newF2]) {
        return;
      }

      const d3Keys = sortedKeys(groupedOptions[newF2]);
      const validD3 =
        thirdDigit && d3Keys.includes(thirdDigit) ? thirdDigit : d3Keys[0];
      const d4Group = groupedOptions[newF2][validD3];
      const d4Keys = d4Group ? sortedKeys(d4Group) : [];
      const validD4 =
        fourthDigit && d4Keys.includes(fourthDigit) ? fourthDigit : d4Keys[0];

      setIsLoading(true);
      setFirstTwo(newF2);
      setThirdDigit(validD3);
      setFourthDigit(validD4);
      commitSelection(newF2, validD3, validD4);
    },
    [thirdDigit, fourthDigit, groupedOptions, commitSelection],
  );

  const onThirdDigitChange = useCallback(
    (opt: SelectDigitOption | null) => {
      const newD3 = opt?.value;
      if (!newD3 || !firstTwo || !groupedOptions[firstTwo]?.[newD3]) {
        return;
      }

      const d4Keys = sortedKeys(groupedOptions[firstTwo][newD3]);
      const validD4 =
        fourthDigit && d4Keys.includes(fourthDigit) ? fourthDigit : d4Keys[0];

      setIsLoading(true);
      setThirdDigit(newD3);
      setFourthDigit(validD4);
      commitSelection(firstTwo, newD3, validD4);
    },
    [firstTwo, fourthDigit, groupedOptions, commitSelection],
  );

  const onFourthDigitChange = useCallback(
    (opt: SelectDigitOption | null) => {
      const newD4 = opt?.value;
      if (!newD4 || !firstTwo || !thirdDigit) {
        return;
      }

      setIsLoading(true);
      setFourthDigit(newD4);
      commitSelection(firstTwo, thirdDigit, newD4);
    },
    [firstTwo, thirdDigit, commitSelection],
  );

  return {
    isLoading,
    setIsLoading,
    firstTwo,
    thirdDigit,
    fourthDigit,
    firstTwoDigitOptions,
    thirdDigitOptions,
    fourthDigitOptions,
    onFirstTwoChange,
    onThirdDigitChange,
    onFourthDigitChange,
  };
}
